import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, Zap, Lock } from 'lucide-react';
import { Mascot } from '@/components/Mascot';
import { fetchPromoOffer, untilLabel, type PromoOffer } from '@/lib/promo';
import { startDay } from '@/lib/dofiStart';
import { motion } from 'framer-motion';
import { useUserStore } from '@/store/userStore';
import { MySummaryCard } from '@/components/MySummaryCard';
import { useTelegram } from '@/hooks/useTelegram';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
// Базовый URL магазина Prodamus (динамическая ссылка с параметрами).
// ВАЖНО: короткие ссылки-формы (payform.ru/j7bsnBM) СРЕЗАЛИ customer_extra —
// из-за этого оплатившие не получали доступ. Формат ниже подтверждён поддержкой Prodamus.
const SHOP_URL = 'https://kokorinpay.payform.ru/';
// ═══ Подача от Дофи, а не от счётчика (решение Славы 30.10.2026) ═══
// За счётчик калорий не платят — бесплатных десятки. Платят за то, что тебя
// ведут. Поэтому на экране оплаты нет ни слова про калории, а главное —
// моменты, в которых человек узнаёт себя. Сравнение с личной работой Славы
// НЕ ставим: холодная аудитория его не знает, а рядом с ценой приложения
// это его обесценивает. Специалистов не ругаем: сравниваем доступность,
// а не качество (некорректное сравнение запрещено законом о рекламе).
const MOMENTS = [
  'Когда в 22:40 рука тянется к сладкому — Дофи спросит: это голод или день был тяжёлый?',
  'Когда сорвался — не отругает, а разберёт, что было до этого.',
  'Когда встал на весы и опустились руки — покажет, что изменилось на самом деле.',
  'Когда не знаешь, что съесть — посмотрит твой день и подскажет из того, что у тебя есть.',
  'Когда опять хочется бросить — напомнит, ради чего ты начинал.',
];
const FEATURES = [
  'Задания и разбор каждой недели под твой тип пищевого поведения',
  'Дофи помнит тебя, видит дневник и замечает то, чего не видишь ты',
  'Еда по фото, тексту и этикетке — за секунды, без весов',
  'Витамины и минералы в твоём рационе',
  'Дыхание, музыка и практика самоподдержки',
];
const NEXT_STEP: Record<string, string> = {
  emotional: 'вечера без заедания',
  external: 'среда работает за тебя',
  restrained: 'еда без войны',
  balanced: 'находим лишнее без борьбы',
};
export default function Paywall() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<'month' | 'year'>('year');
  const [offer, setOffer] = useState<PromoOffer | null>(null);
  useEffect(() => { fetchPromoOffer().then(setOffer); }, []);
  if (!profile) return null;
  // Срок цены первого месяца — с сервера (068). Нет ответа (старая база) — как раньше.
  const isPromo = !profile.used_promo && (offer ? offer.available : true);
  const ep = profile.eating_profile ?? null;
  const startAt = (profile as any).dofi_start_at as string | null | undefined;
  const passedStart = !!startAt && startDay(startAt) >= 3;
  // Те же числа сверяет prodamus-webhook (resolveTariff, 11.09.2026): оплата
  // меньше тарифа доступа не даёт. Менять цену — здесь, в вебхуке и в оферте разом.
  const YEAR_PRICE = 5990;
  // Первый месяц 990, а не 550 (решение Славы 04.09.2026). Барьер входа
  // снимают три бесплатных дня, а не скидка, поэтому 550 не привлекало —
  // оно обесценивало продукт и создавало обрыв +171% на втором месяце.
  // 990 -> 1490 это +51%, и рядом с личной работой коуча цена не выглядит игрушечной.
  const monthPrice = isPromo ? 990 : 1490;
  const price = plan === 'year' ? YEAR_PRICE : monthPrice;
  const expiresAt = profile.subscription_expires_at
    ? new Date(profile.subscription_expires_at)
    : null;
  const isExpired = expiresAt ? expiresAt < new Date() : true;
  const daysLeft = expiresAt
    ? Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0;
  function pay() {
    haptic('medium');
    setLoading(true);
    const params = new URLSearchParams();
    params.set('do', 'pay');
    // order_id — чистый уникальный код (Prodamus показывает его в поле ФИО).
    // НЕ используем UUID профиля тут (иначе в ФИО «кракозябры»). Матчинг юзера — по customer_extra ниже.
    params.set('order_id', `SH${Date.now()}${Math.floor(Math.random() * 1000)}`);
    // Товар с фиксированной суммой (промо 990 / обычная 1490)
    params.set('products[0][price]', String(price));
    params.set('products[0][quantity]', '1');
    params.set('products[0][name]', plan === 'year'
      ? 'Годовая подписка Slava Health: питание, привычки, тренировки — 12 месяцев'
      : 'Подписка на приложение Slava Health: питание, привычки, тренировки — 30 дней');
    // Фискализация чека (54-ФЗ), числовые коды Prodamus:
    // paymentObject=4 → услуга; paymentMethod=4 → полная оплата в момент передачи
    params.set('products[0][paymentObject]', '4');
    params.set('products[0][paymentMethod]', '4');
    // Ключевое: customer_extra = UUID профиля → вебхук находит юзера железно
    params.set('customer_extra', profile!.id);
    // Email НЕ подставляем — у нас он фейковый (@telegram.user), чек бы не дошёл.
    // Клиент вводит реальный телефон/email на форме Prodamus → получает кассовый чек (54-ФЗ).
    // Юзер всё равно находится по customer_extra (UUID), email для сверки не нужен.
    // callbackType=json → вебхук приходит в JSON, подпись сверяется корректно
    params.set('callbackType', 'json');
    params.set('urlReturn', window.location.origin + '/profile');
    params.set('urlSuccess', window.location.origin + '/profile?paid=1');
    const url = `${SHOP_URL}?${params.toString()}`;
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.openLink) {
      tg.openLink(url, { try_instant_view: false });
    } else {
      window.open(url, '_blank');
    }
    setTimeout(() => setLoading(false), 2000);
  }
  return (
    <div className="min-h-screen bg-bg pb-8">
      <div className="max-w-md mx-auto px-5 pt-6">
        <button
          onClick={() => nav(-1)}
          className="flex items-center gap-1 text-ink-muted hover:text-ink mb-6 text-sm"
        >
          <ArrowLeft size={16} /> Назад
        </button>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <Mascot pose={passedStart ? 'celebrate' : 'welcome'} className="w-28 h-28 mx-auto mb-3" />
          <h1 className="font-display text-[26px] leading-tight font-bold mb-2 text-balance">
            {passedStart
              ? `Ты прошёл старт. Дальше — 7 дней: ${NEXT_STEP[ep?.style ?? 'balanced']}`
              : 'Тот, кто рядом, когда сложно'}
          </h1>
          <p className="text-ink-muted leading-relaxed text-sm">
            {passedStart
              ? 'Дофи уже знает твой тип и твой ритм. Следующие задания — под тебя.'
              : isExpired
                ? 'Продолжим? Всё, что ты собрал, сохранено.'
                : `Доступ ещё ${daysLeft} ${daysLeft === 1 ? 'день' : 'дней'}. Можно продлить заранее.`}
          </p>
        </motion.div>

        {/* Моменты, в которых человек узнаёт себя (боли из практики Славы) */}
        <div className="card p-4 mb-5 space-y-2.5">
          {passedStart && <div className="font-display font-bold text-sm mb-1">Тот, кто рядом, когда сложно</div>}
          {MOMENTS.map((m) => (
            <div key={m} className="flex gap-2.5 text-sm leading-relaxed text-ink">
              <span className="text-emerald-glow shrink-0">•</span><span>{m}</span>
            </div>
          ))}
          <p className="text-xs text-ink-muted pt-1">Он помнит тебя, видит твой дневник и замечает то, чего не замечаешь ты сам.</p>
        </div>

        {/* Доступность, а не качество: специалистов не ругаем */}
        <div className="rounded-2xl border border-line p-4 mb-5 text-sm leading-relaxed text-ink-muted">
          Консультация специалиста — от 3 000 ₽ за час, раз в неделю или две. Но срыв случается не на приёме. Он случается в 23:00 у холодильника.{' '}
          <span className="text-ink font-medium">Дофи рядом именно тогда.</span>
        </div>

        {/* Что человек уже собрал — ДО цены. Он решает не «нужно ли мне
            приложение», а «выбрасывать ли то, что уже накопилось». */}
        <MySummaryCard />

        {/* Селектор тарифа: Месяц / Год */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="flex gap-2 p-1 bg-bg-elevated rounded-2xl mb-5"
        >
          <button
            onClick={() => { haptic('light'); setPlan('month'); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${plan === 'month' ? 'bg-bg text-ink shadow-sm' : 'text-ink-dim'}`}
          >
            Месяц
          </button>
          <button
            onClick={() => { haptic('light'); setPlan('year'); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${plan === 'year' ? 'bg-bg text-ink shadow-sm' : 'text-ink-dim'}`}
          >
            Год
            <span className="px-1.5 py-0.5 rounded-full bg-emerald-glow/15 text-emerald-glow text-[10px] font-bold">499 ₽/мес</span>
          </button>
        </motion.div>

        {isPromo && plan === 'month' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="card-elevated border-warn/40 bg-warn/5 p-4 mb-5 relative overflow-hidden"
          >
            <div className="absolute top-2 right-2">
              <Zap size={16} className="text-warn" />
            </div>
            <div className="text-[10px] font-bold tracking-wider text-warn uppercase mb-1">
              🎁 Специальное предложение
            </div>
            <div className="font-display font-bold text-base mb-1">
              Первый месяц 990 ₽{offer?.until ? ` — ${untilLabel(offer.until)}` : ''}
            </div>
            <div className="text-xs text-ink-muted">
              {offer?.until ? 'Потом — 1 490 ₽. Цена первого месяца действует только сейчас.' : 'Вместо 1490 ₽. Только для новых пользователей.'}
            </div>
          </motion.div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-elevated border-emerald-glow/40 p-5 mb-5"
        >
          <div className="text-center">
            <div className="label mb-2">{plan === 'year' ? 'Годовая подписка' : 'Стоимость подписки'}</div>
            <div className="flex items-baseline justify-center gap-2 mb-2">
              <span className="font-display text-5xl font-bold text-emerald-glow">{price}</span>
              <span className="text-xl text-ink-muted">₽{plan === 'year' ? '/год' : ''}</span>
            </div>
            {/* Годовой подаётся как СПОСОБ ПЛАТИТЬ, а не как скидка.
                Раньше здесь висело «17 880 ₽ зачёркнуто · экономия 11 890» —
                и это подсвечивало, что месячная цена ненастоящая (разрыв 66%,
                проблема из журнала от 10.08). Теперь сравниваются два способа
                платить: 499 ₽/мес сразу за год против 1490 ₽ помесячно. */}
            {plan === 'year' && (
              <div className="text-sm text-emerald-glow font-medium">
                499 ₽ в месяц при оплате сразу за год
              </div>
            )}
            {plan === 'month' && isPromo && (
              <div className="flex items-center justify-center gap-2 text-sm">
                <span className="text-ink-dim line-through">1490 ₽</span>
                <span className="px-2 py-0.5 rounded-full bg-emerald-glow/15 text-emerald-glow text-xs font-bold">
                  первый месяц
                </span>
              </div>
            )}
            {plan === 'month' && !isPromo && (
              <div className="text-sm text-emerald-glow font-medium">Это 50 ₽ в день — меньше шоколадки</div>
            )}
            <div className="text-xs text-ink-dim mt-3">
              {plan === 'year'
                ? 'на 12 месяцев · можно в рассрочку'
                : isPromo ? '1-й месяц со скидкой, далее 1490 ₽/мес' : 'доступ на 1 месяц'}
            </div>
          </div>
        </motion.div>
        {/* Бонус за оплату (решение Славы 30.10.2026). Видеоурок появится,
            когда Слава его запишет, — тогда добавить вторым пунктом. */}
        {ep && (
          <div className="card-elevated border-emerald-glow/30 bg-emerald-glow/5 p-4 mb-5">
            <div className="text-[10px] font-bold tracking-wider text-emerald-glow uppercase mb-1">Бонус к подписке</div>
            <div className="font-display font-bold text-sm mb-1">Расширенный портрет пищевого поведения</div>
            <div className="text-xs leading-relaxed text-ink-muted">
              Все три стороны твоего пищевого поведения, ситуации, в которых тебя тянет к еде, и твой ритм по дневнику — с первым шагом под тебя.
            </div>
          </div>
        )}

        {/* Что сохранится, а что встанет — честно: данные не удаляются */}
        <div className="card p-4 mb-5 text-sm leading-relaxed">
          <div className="flex gap-2.5 mb-2"><Check size={16} className="text-emerald-glow shrink-0 mt-0.5" /><span className="text-ink-muted">Твой дневник, тест и всё, что Дофи о тебе знает, сохранятся.</span></div>
          <div className="flex gap-2.5"><Lock size={16} className="text-warn shrink-0 mt-0.5" /><span className="text-ink-muted">Новые задания, разборы и разговоры с Дофи — с подпиской.</span></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-5 mb-5"
        >
          <div className="font-display font-bold mb-3 text-sm">Что входит:</div>
          <div className="space-y-2.5">
            {FEATURES.map((f) => (
              <div key={f} className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full bg-emerald-glow/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={12} className="text-emerald-glow" />
                </div>
                <span className="text-sm leading-relaxed text-ink-muted">{f}</span>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          onClick={pay}
          disabled={loading}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-glow to-sky-glow text-bg font-display font-bold text-base shadow-lg active:scale-[0.98] transition-all disabled:opacity-50"
        >
          {loading ? 'Открываем оплату…' : `Оплатить ${price} ₽`}
        </motion.button>
        <p className="text-center text-[11px] text-ink-dim mt-3 leading-relaxed">
          Оплачивая, ты соглашаешься с условиями. Оплата разовая — доступ на {plan === 'year' ? '12 месяцев' : '30 дней'}, автопродления нет.
        </p>
        {!isExpired && expiresAt && (
          <div className="mt-6 text-center text-xs text-ink-dim">
            Действует до {format(expiresAt, 'd MMMM yyyy', { locale: ru })}
          </div>
        )}
        
        {/* Подпись бренда */}
        <div className="text-center mt-8">
          <div className="text-[11px] text-ink-dim font-medium">
            Slava Health
          </div>
          <div className="text-[10px] text-ink-dim/70 mt-0.5">
            Помощник по питанию · by Kokorin Health
          </div>
        </div>
      </div>
    </div>
  );
}
